import React, { ChangeEvent, FC, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import Modal from 'components/common/Modal';
import { Button, ButtonVariant } from 'components/common/buttons/Button';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { IndividualPayrocTerminalAccount } from 'api/admin/stores/editPayrocTerminalAccount';
import { usePayrocMerchants } from '../../businesses/payrocAccounts/queries/usePayrocMerchants';
import styles from './EditTerminalAccountModal.module.scss';
import { useAddPayrocTerminal } from './mutations/useAddPayrocTerminal';
import { useEditPayrocTerminal } from './mutations/useEditPayrocTerminal';

export const EditTerminalAccountModal: FC<{
  isNew: boolean;
  storeId: number;
  businessId: number;
  terminal: IndividualPayrocTerminalAccount | null;
  onClose: (shouldReload: boolean) => void;
}> = ({ isNew, storeId, businessId, terminal, onClose }) => {
  const [terminalId, setTerminalId] = useState<string | null>(terminal?.terminalId ?? null);
  const [payrocMerchantAccountId, setPayrocMerchantAccountId] = useState<number | null>(
    terminal?.payrocMerchantAccountId ?? null,
  );

  const { isLoading, data: merchantAccounts } = usePayrocMerchants(businessId);
  const { mutateAsync: onAddPayrocTerminal } = useAddPayrocTerminal();
  const { mutateAsync: onEditPayrocTerminal } = useEditPayrocTerminal();

  const merchantAccountOptions = useMemo(
    () => merchantAccounts.map(({ id, merchantId }) => ({ value: id, display: merchantId })),
    [merchantAccounts],
  );

  const selectedMerchantAccount = useMemo(
    () => merchantAccountOptions.find((option) => option.value === payrocMerchantAccountId),
    [merchantAccountOptions, payrocMerchantAccountId],
  );

  const handleTerminalIdChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setTerminalId(value);
  };

  const handleMerchantChange = (event = null, value) => {
    setPayrocMerchantAccountId(Number(value.value));
  };

  const handleSave = async () => {
    if (isNew) {
      await onAddPayrocTerminal({ storeId, terminalId, payrocMerchantAccountId });
    } else {
      await onEditPayrocTerminal({
        storeId,
        terminalId,
        payrocMerchantAccountId,
        payrocTerminalAccountId: terminal?.id,
      });
    }

    onClose(true);
  };

  return (
    <Modal modalHeight="370px" onClose={() => onClose(false)}>
      <div className={styles.editTerminalFormContainer}>
        <h1 className={styles.editTerminalFormContainerHeader}>
          {`${isNew ? 'Add' : 'Edit'} Terminal Account`}
        </h1>
        <div className={styles.editTerminalFormContainerRow}>
          <p>Merchant Account</p>
          {isLoading && <CentsLoader />}
          {!isLoading && (
            <Autocomplete
              disablePortal
              id="autocomplete-choose-merchant-account"
              getOptionLabel={(option) => option.display}
              options={merchantAccountOptions}
              sx={{ width: '300px' }}
              onChange={handleMerchantChange}
              renderInput={(params) => <TextField {...params} />}
              value={selectedMerchantAccount}
              isOptionEqualToValue={(option, value) => option.value === value.value}
            />
          )}
        </div>
        <div className={styles.editTerminalFormContainerRow}>
          <p>Terminal Id</p>
          <TextField
            variant="outlined"
            type="number"
            value={terminalId}
            sx={{ width: '300px' }}
            onChange={handleTerminalIdChange}
          />
        </div>
        <Button
          isDisabled={isEmpty(terminalId) || !payrocMerchantAccountId}
          type={ButtonVariant.CONTAINED}
          onClick={handleSave}
          style={{ width: '150px' }}
        >
          {`${isNew ? 'Add' : 'Save'}`}
        </Button>
      </div>
    </Modal>
  );
};
